import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../config/firebase';
import { GifLoader } from '../components/website/GifLoader';

/**
 * Handles redirect after returning from Canva OAuth/config.
 * Flow:
 * 1) Extract correlation_jwt from URL parameters
 * 2) Make POST request to Canva return-nav API with correlation_jwt
 * 3) Redirect to data.redirectionUrl from API response
 *
 * Notes:
 * - This replaces the old bancale logic that fetched posts and navigated to edit page
 * - Now uses a clean API call to handle the Canva return flow
 */
const ReturnFromCanva: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCanvaReturn = async () => {
      try {
        // Extract correlation_jwt from URL parameters
        const correlationJwt = searchParams.get('correlation_jwt');

        if (!correlationJwt) {
          setError('Missing correlation_jwt parameter');
          return;
        }

        // Get current Firebase user
        const firebaseUser = auth.currentUser;
        if (!firebaseUser || !firebaseUser.email) {
          setError('User not authenticated');
          return;
        }

        // Get Firebase token for authorization
        const idToken = await firebaseUser.getIdToken();

        // Make POST request to Canva return-nav API
        const response = await fetch('https://api.socialness.ai/management/integrations/canva/return-nav', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            correlation_jwt: correlationJwt
          })
        });

        if (!response.ok) {
          throw new Error(`API call failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (!data.success || !data.data?.redirectionUrl) {
          throw new Error('Invalid API response: missing redirectionUrl');
        }

        // Store exportUrl in sessionStorage for MediaPostCard to use
        if (data.data.exportUrl) {
          sessionStorage.setItem('canva_export_url', data.data.exportUrl);
        }

        // Redirect to the specified URL
        navigate(data.data.redirectionUrl, { replace: true });

      } catch (err) {
        console.error('Error handling Canva return:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        // Fallback to dashboard on error
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 3000);
      }
    };

    handleCanvaReturn();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full space-y-4 p-8 bg-white rounded-lg shadow-lg border border-red-200">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {i18n.t('canvaReturn.error.title', 'Canva Integration Error')}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {error}
            </p>
            <p className="text-xs text-gray-500">
              {i18n.t('canvaReturn.error.redirecting', 'Redirecting to dashboard...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <GifLoader
        title={i18n.t('canvaReturn.loading.title', 'Processing Canva Integration...')}
        currentStep={i18n.t('canvaReturn.loading.processing', 'Connecting your design...')}
        videoSrc="/Panda - Gold Digger.mov"
      />
    </div>
  );
};

export default ReturnFromCanva;


